import React from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import MainLayout from '@/components/layout/MainLayout'
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout'
import Login from '@/pages/Login'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import Dashboard from '@/pages/Dashboard'
import ServicesManagement from '@/pages/content/ServicesManagement'
import LocationsManagement from '@/pages/content/LocationsManagement'
import BlogManagement from '@/pages/content/BlogManagement'
import StaticPagesManagement from '@/pages/content/StaticPagesManagement'
import ProjectsManagement from '@/pages/content/ProjectsManagement'
import ProjectPagesManagement from '@/features/project-pages/pages/ProjectPagesManagement'
import ProjectPageCreate from '@/features/project-pages/pages/ProjectPageCreate'
import ProjectPageEdit from '@/features/project-pages/pages/ProjectPageEdit'
import LeadsManagement from '@/features/customers/leads/pages/LeadsManagementPage'
import LeadAttributionDashboard from '@/features/customers/attribution/pages/LeadAttributionPage'
import CustomerReviews from '@/features/customers/reviews/pages/CustomerReviewsPage'
import CustomerDataPage from '@/features/customers/data/pages/CustomerDataPage'
import JobberCRMPage from '@/features/customers/jobber/pages/JobberCRMPage'
import ReviewsManagement from '@/pages/integrations/gmb/ReviewsManagement'
import GMBReviews from '@/pages/integrations/gmb/GMBReviews'
import GalleryManagement from '@/pages/media/GalleryManagement'
import GalleryDetailPage from '@/pages/media/GalleryDetailPage'
import GalleryReorderPage from '@/pages/media/GalleryReorderPage'
// Gallery detail sub-pages (nested under GalleryLayout)
import GalleryOverviewPage from '@/features/gallery/pages/GalleryOverviewPage'
import GalleryReviewPage from '@/features/gallery/pages/GalleryReviewPage'
import GalleryJobPage from '@/features/gallery/pages/GalleryJobPage'
import GalleryExportPage from '@/features/gallery/pages/GalleryExportPage'
import ApprovalQueue from '@/pages/media/ApprovalQueue'
import Assets from '@/pages/media/Assets'
import SocialMediaManagerDirect from '@/pages/marketing/SocialMediaManagerDirect'
import CreatePost from '@/pages/marketing/CreatePost'
import ContentCalendar from '@/pages/marketing/ContentCalendar'
import IntegrationsOverview from '@/pages/integrations/IntegrationsOverview'
import GMBLayout from '@/pages/integrations/gmb/GMBLayout'
import GMBOverview from '@/pages/integrations/gmb/GMBOverview'
import GMBSetup from '@/pages/integrations/GMBSetup'
import GMBPosts from '@/pages/integrations/gmb/GMBPosts'
import GMBQuestions from '@/pages/integrations/gmb/GMBQuestions'
import GMBInsights from '@/pages/integrations/gmb/GMBInsights'
import GMBSync from '@/pages/integrations/gmb/GMBSync'
import GMBDebug from '@/pages/integrations/gmb/GMBDebug'
import SchedulerConfigPage from '@/features/scheduler/pages/SchedulerConfigPage'
import Users from '@/pages/Users'
import AcceptInvitation from '@/pages/AcceptInvitation'
import ThemePreview from '@/pages/design/ThemePreview'

// Placeholder pages for features not yet implemented
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
      <p className="text-muted-foreground">
        This page is under construction. Features will be implemented in upcoming phases.
      </p>
    </div>
    <div className="bg-muted/20 border-2 border-dashed border-muted rounded-lg p-12 text-center">
      <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
      <p className="text-muted-foreground">
        {title} functionality will be available once the merger is complete.
      </p>
    </div>
  </div>
)

const router = createBrowserRouter([
  // Public routes
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/auth/accept-invite', element: <AcceptInvitation /> },

  // Design system routes (dev tools)
  { path: '/design/theme-preview', element: <ThemePreview /> },

  // Protected routes with layout
  {
    path: '/',
    element: (
      <AuthenticatedLayout>
        <MainLayout />
      </AuthenticatedLayout>
    ),
    children: [
      { index: true, element: <Dashboard /> },

      // Content Management
      {
        path: 'content',
        children: [
          { path: 'services', element: <ServicesManagement /> },
          { path: 'locations', element: <LocationsManagement /> },
          { path: 'blog', element: <BlogManagement /> },
          { path: 'pages', element: <StaticPagesManagement /> },
          { path: 'projects', element: <ProjectsManagement /> },
          { path: 'project-pages', element: <ProjectPagesManagement /> },
          { path: 'project-pages/new', element: <ProjectPageCreate /> },
          { path: 'project-pages/:id/edit', element: <ProjectPageEdit /> },
        ],
      },

      // Media & Gallery
      {
        path: 'media',
        children: [
          { path: 'galleries', element: <GalleryManagement /> },
          {
            path: 'galleries/:id',
            element: <GalleryDetailPage />,
            children: [
              { index: true, element: <GalleryOverviewPage /> },
              { path: 'review', element: <GalleryReviewPage /> },
              { path: 'job', element: <GalleryJobPage /> },
              { path: 'export', element: <GalleryExportPage /> },
            ],
          },
          { path: 'galleries/:id/reorder', element: <GalleryReorderPage /> },
          { path: 'approval', element: <ApprovalQueue /> },
          { path: 'assets', element: <Assets /> },
        ],
      },

      // Customers
      {
        path: 'customers',
        children: [
          { path: 'leads', element: <LeadsManagement /> },
          { path: 'attribution', element: <LeadAttributionDashboard /> },
          { path: 'reviews', element: <CustomerReviews /> },
          { path: 'data', element: <CustomerDataPage /> },
          { path: 'jobber', element: <JobberCRMPage /> },
        ],
      },

      // Marketing & Social
      {
        path: 'marketing',
        children: [
          { path: 'social', element: <SocialMediaManagerDirect /> },
          { path: 'social/create', element: <CreatePost /> },
          { path: 'calendar', element: <ContentCalendar /> },
        ],
      },

      // Integrations
      {
        path: 'integrations',
        children: [
          { index: true, element: <IntegrationsOverview /> },
          {
            path: 'gmb',
            element: <GMBLayout />,
            children: [
              { index: true, element: <GMBOverview /> },
              { path: 'reviews', element: <ReviewsManagement /> },
              { path: 'sync-reviews', element: <GMBReviews /> },
              { path: 'posts', element: <GMBPosts /> },
              { path: 'insights', element: <GMBInsights /> },
              { path: 'qa', element: <GMBQuestions /> },
              { path: 'locations', element: <PlaceholderPage title="GMB Locations" /> },
              { path: 'sync', element: <GMBSync /> },
              { path: 'debug', element: <GMBDebug /> },
            ],
          },
          { path: 'gmb/setup', element: <GMBSetup /> },
          { path: 'analytics', element: <PlaceholderPage title="Analytics Integration" /> },
        ],
      },

      // Scheduler
      { path: 'scheduler', element: <SchedulerConfigPage /> },

      // Admin
      {
        path: 'admin',
        children: [
          { path: 'users', element: <Users /> },
          { path: 'security', element: <PlaceholderPage title="Security & Audit" /> },
          { path: 'settings', element: <PlaceholderPage title="Site Settings" /> },
        ],
      },
    ],
  },
])

export default function App() {
  return <RouterProvider router={router} />
}
